import { ref } from "vue"
import { defineStore, storeToRefs } from "pinia"
import type { Answers, Answer } from "@/features/shape/store/shape.types.js"
import {
  initAnswers,
  initExtra,
  factoryPatch,
  factoryReset,
} from "@/features/shape/store/shape.data.js"
import { useToast } from "vue-toastification"
import { useI18nStore } from "@/stores/i18n"
import { useUserStore } from "@/stores/user"
import {
  getAssessment,
  patchAssessment,
  postAssessment,
  deleteAssessment,
  getSwipeAssessment,
} from "@/api/viktor/assessment/assessment"
import { getVersionFromLang } from "@/features/shape/helpers/getVersionFromLang"
import { useQuestionsStore } from "@/features/shape/store/questions.store"
import { getDeviceType } from "@/helpers/getDeviceType"
import { shapeActor } from "@/features/shape/machines/shapeMachine"
import { MixpanelInstance } from "@/plugins/mixpanel/mixpanel"
import { sleep } from "@/lib/utils"

export const useAnswersStore = defineStore("shapeAnswersStore", () => {
  const { goNext, setCurrentIndex } = useQuestionsStore()
  const i18nStore = useI18nStore()
  const { updateLocale, getLocaleIdFromIsoCode } = i18nStore
  const { currentLocale } = storeToRefs(useI18nStore())
  const { isFromInt } = storeToRefs(useUserStore())

  const answers = ref<Answers>(initAnswers)
  const isPatchExtraLoading = ref(false)

  const userStore = useUserStore()

  const toast = useToast()

  // GETTERS

  async function fetchShape() {
    const assessments = userStore.isPoleEmploi
      ? userStore.assessmentPE.assessments
      : userStore.assessmentSummary.assessments

    if (
      assessments.shape?.status === "finish" &&
      !assessments.shape?.can_restart &&
      !assessments.shape?.retake_test &&
      !assessments.shape?.not_passed
    ) {
      // si le test est terminé, on sort de l'assessment
      if (isFromInt.value) answers.value.status = "thank-you"
      else window.location.reload()
    } else if (assessments.shape?.status === null) {
      // si le test n'a jamais été commencé on l'initalise
      await initShape(false, null)
    } else if (
      assessments.shape?.status === "finish" &&
      (assessments.shape?.can_restart || assessments.shape?.retake_test)
    ) {
      // si le status est finish et qu'on est soit sur une demande de retake test pour PE
      // ou sur un test datant de plus d'un an
      await deleteAssessment("shape")
      await initShape(false, null)
    } else if (
      assessments.shape?.status === "todo" ||
      assessments.shape?.status === "inprogress" ||
      !!assessments.shape?.not_passed
    ) {
      // autrement c'est que le test est en cours
      // donc on récupère les données du test et on popule les stores
      const assessment = await getAssessment("shape")
      answers.value = assessment
      if (Object.keys(answers.value?.data || {}).length === 0) {
        setCurrentIndex(0)
        answers.value.data.extra = initAnswers.data.extra
      } else {
        setCurrentIndex(answers.value?.data?.items?.length)
      }

      // sometimes version was not set in the assessment
      let versionFull
      if (answers.value?.version === null) {
        versionFull = getVersionFromLang(currentLocale.value.iso_code)
        answers.value.version = versionFull
      } else {
        versionFull = answers.value.version
      }

      const lastUnderscoreIndex = versionFull.lastIndexOf("_")
      const langCode = versionFull.substring(0, lastUnderscoreIndex)
      userStore.audio = answers.value.data?.extra?.general_audio ? true : false
      answers.value.data.extra.elapsed_time = assessment.elapsed_time
      answers.value.data.extra.finished = assessment.finished_at ? true : false
      answers.value.data.extra.native_language = assessment.native_language
      answers.value.data.extra.wholesome_conditions =
        assessment.wholesome_conditions

      if (
        langCode !== currentLocale.value.iso_code &&
        answers.value?.status === "inprogress"
      ) {
        const locale_id = getLocaleIdFromIsoCode(langCode)
        await updateLocale(locale_id)
        // @TODO: set trad key from luciano
        toast.info("Language change")
      }
    }

    return answers.value
  }

  function getElapsedTime() {
    let time = 0
    answers.value.data.items.map((item) => (time += item.time_initial))
    return Math.round(time)
  }

  async function doublecheckShapeOrSwipe() {
    let alreadyPassedSwipe = false

    const shape = userStore.assessmentSummary.assessments.shape
    if (shape && !shape.status) {
      const getSwipe = await getSwipeAssessment()
      alreadyPassedSwipe = Array.isArray(getSwipe)
        ? getSwipe.length > 0
        : Object.keys(getSwipe).length > 0
    }

    return alreadyPassedSwipe
  }

  // ACTIONS

  async function initShape(restarting?: boolean, lang?: number | null) {
    const payload: Answers = {
      version: getVersionFromLang(currentLocale.value.iso_code),
      locale_id: lang ? lang : currentLocale.value.id,
      status: "todo",
      data: { items: [], extra: { ...initExtra, isRestarting: restarting } },
    }
    const data = await postAssessment("shape", payload)
    answers.value = payload
    setCurrentIndex(0)

    return data
  }

  async function submitAnswer(answer: string, time: number) {
    if (
      !answers.value.data ||
      (Array.isArray(answers.value.data) && answers.value.data.length === 0)
    ) {
      answers.value.data = { items: [], extra: initExtra }
    }

    // split answer string by _ to get the question id
    const proposal = answer.split("_proposal_")[1]
    const proposalId = "psv20_proposal_" + proposal.split("_")[0]

    // check if the answer has been already answered from answer param starts with
    const alreadyAnswered = answers.value.data.items.find((a) =>
      a.answer.includes(proposalId)
    )
    if (!alreadyAnswered) {
      const item: Answer = {
        answer: answer,
        audio: userStore.audio,
        device_type: getDeviceType(),
        screen_width: screen.width,
        time_initial: time,
      }
      answers.value.data.items.push(item)

      answers.value.data.extra.general_audio = userStore.audio

      const payload: Answers = {
        version: answers.value.version,
        locale_id: currentLocale.value.id,
        data: answers.value.data,
      }
      await patchAssessment("shape", payload)
    }
    await goNext()
  }

  async function patchExtra(type: string, answer: boolean) {
    isPatchExtraLoading.value = true
    try {
      answers.value.data.extra[type] = answer
      answers.value.data.extra.elapsed_time = getElapsedTime()

      const payload: Answers = {
        version: answers.value.version,
        locale_id: currentLocale.value.id,
        data: answers.value.data,
      }
      await patchAssessment("shape", payload)
    } finally {
      await sleep(300) // Timeout to prevent flicker
      isPatchExtraLoading.value = false
    }
  }

  async function patchRestart(
    type: string,
    answer: boolean,
    lang?: number | null
  ) {
    // try to submit final results
    try {
      await submitFinalResults(lang)
    } catch {
      return
    }

    // then, if user wants to restart,
    if (answer) {
      // delete active shape
      await deleteAssessment("shape")

      // set param isRestarting
      answers.value.data.extra.isRestarting = answer

      // init a brand new assessment with isRestarting param = true
      // isRestarting?: boolean, lang?: number | null
      await initShape(true, lang ? lang : null)
      lang && (await updateLocale(lang))

      // finally, send user to the right state (now or later)
      if (type === "now") {
        return shapeActor.send({ type: "now" })
      } else {
        return shapeActor.send({ type: "later" })
      }
    } else {
      return shapeActor.send({ type: "done" })
    }
  }

  async function submitFinalResults(lang?: number | null) {
    try {
      answers.value.data.extra.elapsed_time = getElapsedTime()
      answers.value.data.extra.finished = true
      const payload: Answers = {
        version: answers.value.version,
        locale_id: lang || currentLocale.value.id,
        data: answers.value.data,
      }

      // ultime patch to finish the test
      await patchAssessment("shape", payload)

      MixpanelInstance.track("shape_finish", {
        payload: payload,
      })

      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }

  async function patchFactoryTest(isRestarting: boolean) {
    const payload = factoryPatch(isRestarting ? isRestarting : undefined) // Leave undefined as default
    await patchAssessment("shape", payload)
    window.location.reload()
  }

  async function resetUserDataLight() {
    await patchAssessment("shape", factoryReset)
    window.location.reload()
  }

  return {
    answers,
    fetchShape,
    doublecheckShapeOrSwipe,
    submitAnswer,
    patchExtra,
    isPatchExtraLoading,
    patchRestart,
    submitFinalResults,
    resetUserDataLight,
    patchFactoryTest,
  }
})
